import { useEffect } from 'react';

const REVEAL_SELECTOR = [
  '.section-title',
  '.service-card',
  '.benefit-card',
  '.package-card',
  '.contact-card',
  '.about-inner',
  '.promo-inner',
  '.download-inner',
  '.privacy-section',
  '.terms-section',
].join(',');

const STAGGER_SELECTOR = '.service-card, .benefit-card, .package-card, .contact-card';

// Fades public-page blocks in as they scroll into view. Elements stay visible if IntersectionObserver is missing.
export default function useReveal(deps: unknown[] = []) {
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const els = Array.from(document.querySelectorAll<HTMLElement>(`.fc-site :is(${REVEAL_SELECTOR})`));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    els.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight) return; // already on screen: leave as is
      // Cards in the same grid come in one after another (capped so long grids don't lag).
      if (el.matches(STAGGER_SELECTOR) && el.parentElement) {
        const index = Array.prototype.indexOf.call(el.parentElement.children, el);
        el.style.setProperty('--fc-delay', `${(index % 4) * 80}ms`);
      }
      el.classList.add('fc-reveal');
      observer.observe(el);
    });

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
